/**
 * TODO: Consider moving this file to `@recruitee/interview-events-util` when the `@/interview-events` library is refactored.
 * It belongs to both `events` and `schedule` scopes, so maybe it shouldn't it be in the specific sub-scope but in the main one? (`@/interview-utils`)
 */

import moment from 'moment-timezone';

import type { I18nService } from '@core/i18n-legacy';
import type { InterviewEvent } from '@core/interview-events-types';
import { SpecificVideoProvider, VideoProvider } from '@core/interview-events-types';
import type { IconName, IconSize } from '@core/user-interface-legacy';

type EventKindConfig = {
  eventNameTranslationKey: string;
  nameTranslationKey?: string;
  icon: IconName;
};

const eventKindsConfig: { [key: string]: EventKindConfig } = {
  onsite: {
    eventNameTranslationKey: 'app.interview.kind.onsite',
    icon: 'domain',
  },
  call: {
    eventNameTranslationKey: 'app.interview.kind.call',
    icon: 'call',
  },
  meeting: {
    eventNameTranslationKey: 'app.interview.kind.meeting',
    icon: 'people',
  },
  [`video.${VideoProvider.Google}.${SpecificVideoProvider.GoogleMeet}`]: {
    eventNameTranslationKey: 'app.interview.video_event_name',
    nameTranslationKey: 'app.interview.video_provider.google_meet',
    icon: 'google-meet',
  },
  [`video.${VideoProvider.Google}.${SpecificVideoProvider.GoogleHangouts}`]: {
    eventNameTranslationKey: 'app.interview.video_event_name',
    nameTranslationKey: 'app.interview.video_provider.google_hangouts',
    icon: 'google-meet',
  },
  [`video.${VideoProvider.Google}`]: {
    eventNameTranslationKey: 'app.interview.video_event_name',
    nameTranslationKey: 'app.interview.video_provider.google',
    icon: 'google-meet',
  },
  [`video.${VideoProvider.Zoom}`]: {
    eventNameTranslationKey: 'app.interview.video_event_name',
    nameTranslationKey: 'app.interview.video_provider.zoom',
    icon: 'zoom-us',
  },
  [`video.${VideoProvider.Teams}`]: {
    eventNameTranslationKey: 'app.interview.video_event_name',
    nameTranslationKey: 'app.interview.video_provider.teams',
    icon: 'microsoft-teams',
  },
  [`video.${VideoProvider.Jitsi}`]: {
    eventNameTranslationKey: 'app.interview.video_event_name',
    nameTranslationKey: 'app.interview.video_provider.jitsi',
    icon: 'jitsi',
  },
};

interface EventKindConfigParam {
  kind: string;
  videoProvider: string | null;
  videoUrl: string | null;
}
function eventKindConfig(event: EventKindConfigParam): EventKindConfig {
  let configKey: string;

  if (event.kind !== 'video') {
    configKey = event.kind;
  } else if (event.videoProvider === VideoProvider.Google && event.videoUrl) {
    const specificVideoProvider = (event.videoUrl || '').includes('meet.google.com')
      ? SpecificVideoProvider.GoogleMeet
      : SpecificVideoProvider.GoogleHangouts;
    configKey = `video.${VideoProvider.Google}.${specificVideoProvider}`;
  } else {
    configKey = `video.${event.videoProvider}`;
  }

  return eventKindsConfig[configKey];
}

export function interviewEventName(event: EventKindConfigParam, i18n: I18nService): string {
  const config = eventKindConfig(event);

  if (event.kind !== 'video') {
    return i18n.translate(config.eventNameTranslationKey);
  } else {
    return i18n.translate(config.eventNameTranslationKey, {
      videoProvider: i18n.translate(<string>config.nameTranslationKey),
    });
  }
}

export function interviewKindIcon(event: EventKindConfigParam): IconName {
  return eventKindConfig(event).icon;
}

export function interviewKindIconSize(event: { kind: string }): IconSize {
  if (event.kind === 'video') {
    return 'small';
  } else {
    return 'small-x';
  }
}

export const getFormattedInterval = (
  dateObj: moment.Moment,
  event: { duration: InterviewEvent['duration'] },
): string => `${dateObj.format('LT')} - ${dateObj.add(event.duration, 'm').format('LT')}`;

export const getFormatterDate = (date: string) => moment(date).format('ddd, ll');
